/**
 * broadcastAngularEvent — React-to-AngularJS event bridge
 *
 * Calls eventService.eventBroadcast() on the AngularJS $rootScope
 * via the Angular injector, so React components can trigger AngularJS
 * sidebar refresh events, modal toggles, and other broadcasts.
 *
 * No-ops in standalone mode (no AngularJS to broadcast to).
 * Silently no-ops in embedded mode if AngularJS has not yet bootstrapped.
 */

import { getMountConfig } from '@/mountConfig';

type AngularEventService = {
  eventBroadcast: (evt: { event: string; args?: Record<string, unknown> }) => void;
};

type AngularRootScope = {
  $applyAsync?: () => void;
  $$phase?: string | null;
};

type AngularInjector = {
  get?: (name: string) => unknown;
};

type AngularElement = {
  (selector: string): {
    injector?: () => AngularInjector | undefined;
  };
};

function getInjector(): AngularInjector | undefined {
  try {
    const win = window as unknown as Record<string, unknown>;
    const angular = win.angular as { element?: AngularElement } | undefined;
    return angular?.element?.('[ng-app]')?.injector?.();
  } catch {
    return undefined;
  }
}

/**
 * Broadcast an event to AngularJS via the eventService.
 * Matches: eventService.eventBroadcast({ event, args })
 *
 * Because the broadcast originates from React (outside Angular's digest cycle),
 * any scope mutations performed by listeners would not propagate to bound
 * watchers (e.g., modal-dialog's `$watch('show', ...)`). We schedule a digest
 * via `$applyAsync()` after broadcasting so AngularJS picks up the changes —
 * same pattern used by `setRootScopeProperty` in legacyContext.ts.
 */
export function broadcastAngularEvent(event: string, args?: Record<string, unknown>): void {
  // Skip entirely in standalone mode — no AngularJS to broadcast to
  if (getMountConfig().mode === 'standalone') return;

  try {
    const injector = getInjector();
    if (!injector?.get) return;

    const eventService = injector.get('eventService') as AngularEventService | undefined;
    const rootScope = injector.get('$rootScope') as AngularRootScope | undefined;

    eventService?.eventBroadcast?.({ event, args });

    // Trigger a digest so listener-driven scope changes (e.g. opening modals)
    // actually flow through Angular's watchers.
    if (typeof rootScope?.$applyAsync === 'function') {
      rootScope.$applyAsync();
    }
  } catch {
    // Bridge not available — AngularJS not yet bootstrapped
  }
}
